import React from 'react';
import { useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { INSTALLATION_STATUS_COLORS } from '../../constants/constants';

interface Props {
    data: any[];
    onEdit: (item: any) => void;
    mode?: 'waiting' | 'installation' | 'withoutInstallation';
}

const hashColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#1e3a5f', '#2a5a8a', '#0891b2', '#7c3aed', '#059669', '#d97706', '#dc2626'];
    return colors[Math.abs(hash) % colors.length];
};

const fmtDate = (d: string | null): string => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const getUrgencyColor = (plannedDate: string | null): { color: string; flash: boolean } | null => {
    if (!plannedDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const planned = new Date(plannedDate + 'T00:00:00');
    if (isNaN(planned.getTime())) return null;
    const diffMs = planned.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { color: '#991b1b', flash: true };
    if (diffDays === 0) return { color: '#dc2626', flash: true };
    if (diffDays === 1) return { color: '#ef4444', flash: true };
    if (diffDays <= 3) return { color: '#ea580c', flash: true };
    if (diffDays <= 7) return { color: '#d97706', flash: false };
    return null;
};

const InstallationCardGrid: React.FC<Props> = ({ data, onEdit, mode = 'installation' }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const formatActiveDurationString = (val: string | null | undefined): string => {
        if (!val) return '';
        const str = String(val);
        const dayMatch = str.match(/(\d+)\s*(dit|day|Tag|giorn|día|дена)/i);
        const hourMatch = str.match(/(\d+)\s*(or|hour|Std|ore|hora|час)/i);
        const minMatch = str.match(/(\d+)\s*(min|мин)/i);
        const days = dayMatch ? parseInt(dayMatch[1]) : 0;
        const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
        const minutes = minMatch ? parseInt(minMatch[1]) : 0;
        if (days === 0 && hours === 0 && minutes === 0) return str;
        const parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    };

    if (!data || data.length === 0) {
        return (
            <div className="ccg-grid">
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    <span style={{ marginTop: 12, fontSize: 13 }}>{fm('installation.card.noData', 'Nuk ka të dhëna')}</span>
                </div>
            </div>
        );
    }

    if (mode === 'waiting') {
        return (
            <div className="ccg-grid">
                {data.map((item: any) => {
                    const label = item.warehouseLabel || '';
                    const initials = label.substring(0, 2).toUpperCase() || 'WR';
                    const clientName = item.client?.name || '';
                    const projectName = item.project?.name || '';
                    const productCount = item.products?.length || 0;
                    const plannedColor = item.plannedDateStatus === 'overdue' ? '#ef4444' : item.plannedDateStatus === 'today' ? '#f59e0b' : '#22c55e';

                    return (
                        <MikaCard
                            key={item.id}
                            onClick={() => onEdit(item)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: hashColor(label) }}>{initials}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="ccg-name">{label}</div>
                                        <div className="ccg-company">{clientName}</div>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#1e3a5f', background: '#dbeafe' }}>
                                        {fm('installation.card.productsCount', '{count} produkte', { count: productCount })}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('production.detail.project', 'Projekti')}</span>
                                            <span className="ccg-info-value">{projectName || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.city', 'Qyteti')}</span>
                                            <span className="ccg-info-value">{item.client?.city || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.value', 'Vlera')}</span>
                                            <span className="ccg-info-value">{item.productValue ? `€${item.productValue.toFixed(2)}` : '-'}</span>
                                        </div>
                                        {item.plannedDate && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label">{fm('installation.col.plannedDate', 'Data e planifikuar')}</span>
                                                {(() => {
                                                    var urgency = getUrgencyColor(item.plannedDate);
                                                    var c = urgency ? urgency.color : plannedColor;
                                                    return <span className="ccg-info-value" style={{ color: c, fontWeight: 700, animation: urgency?.flash ? 'urgencyPulse 1.8s ease-in-out infinite' : 'none' }}>{fmtDate(item.plannedDate)}</span>;
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">{fmtDate(item.deliveredAt)}</span>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    if (mode === 'withoutInstallation') {
        return (
            <div className="ccg-grid">
                {data.map((item: any) => {
                    const label = item.label || '';
                    const initials = label.substring(0, 2).toUpperCase() || 'WR';
                    const isCompleted = item.install_status === 'completed';

                    return (
                        <MikaCard
                            key={`${item.source}-${item.id}`}
                            onClick={() => onEdit(item)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: hashColor(label) }}>{initials}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="ccg-name">{label}</div>
                                        <div className="ccg-company">{item.client_name || '-'}</div>
                                    </div>
                                    <span className="ccg-status-badge" style={{
                                        color: isCompleted ? '#16a34a' : '#d97706',
                                        background: isCompleted ? '#f0fdf4' : '#fffbeb',
                                    }}>
                                        {isCompleted ? fm('installation.status.completed', 'E përfunduar') : fm('installation.status.waiting', 'Në pritje')}
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('production.detail.project', 'Projekti')}</span>
                                            <span className="ccg-info-value">{item.project_name || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.city', 'Qyteti')}</span>
                                            <span className="ccg-info-value">{item.client_city || '-'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.source', 'Burimi')}</span>
                                            <span className="ccg-info-value">{item.source === 'transport' ? fm('installation.source.transport', 'Transport') : fm('installation.source.direct', 'Direkt')}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">{fmtDate(item.created_at)}</span>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    // Default: installation mode
    return (
        <div className="ccg-grid">
            {data.map((item: any) => {
                const statusColors = INSTALLATION_STATUS_COLORS[item.status] || { color: '#94a3b8', bg: '#f1f5f9' };
                const clientName = item.client?.name || '-';
                const initials = clientName.substring(0, 2).toUpperCase();

                return (
                    <MikaCard
                        key={item.id}
                        onClick={() => onEdit(item)}
                        header={
                            <div className="ccg-card-header">
                                <div className="ccg-avatar" style={{ background: hashColor(clientName) }}>{initials}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="ccg-name">{clientName}</div>
                                    <div className="ccg-company">{item.project?.name || '-'}</div>
                                </div>
                                <span className="ccg-status-badge" style={{
                                    color: statusColors.color,
                                    background: statusColors.bg,
                                }}>
                                    {(() => {
                                        if (item.status === 1) return fm('installation.strip.inProgress', 'Në proces');
                                        if (item.status === 2) return fm('installation.strip.blocked', 'E bllokuar');
                                        if (item.status === 3) return fm('installation.strip.partial', 'Pjesërisht');
                                        if (item.status === 4) return fm('installation.strip.completed', 'E përfunduar');
                                        return item.statusLabel;
                                    })()}
                                </span>
                            </div>
                        }
                        body={
                            <div className="ccg-card-body-content">
                                <div className="ccg-info-grid">
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('installation.col.installer', 'Instaluesi')}</span>
                                        <span className="ccg-info-value">
                                            {item.isOutsourced ? item.outsourcedCompany : (item.installer?.name || '-')}
                                        </span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('installation.col.activeDuration', 'Koha aktive')}</span>
                                        <span className="ccg-info-value">{item.activeDuration ? formatActiveDurationString(item.activeDuration) : '-'}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('installation.col.value', 'Vlera')}</span>
                                        <span className="ccg-info-value">{item.productValue ? `€${item.productValue.toFixed(2)}` : '-'}</span>
                                    </div>
                                    {item.blockCount > 0 && (
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.blockCount', 'Bllokime')}</span>
                                            <span className="ccg-info-value" style={{ color: '#ef4444', fontWeight: 700 }}>{fm('installation.blockCount.value', '{count}x', { count: item.blockCount })}</span>
                                        </div>
                                    )}
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('installation.col.start', 'Fillimi')}</span>
                                        <span className="ccg-info-value">{fmtDate(item.installationStart)}</span>
                                    </div>
                                    {item.installationEnd && (
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('installation.col.end', 'Përfundimi')}</span>
                                            <span className="ccg-info-value">{fmtDate(item.installationEnd)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                        footer={
                            <div className="ccg-card-footer">
                                <span className="ccg-date">{fmtDate(item.installationStart)}</span>
                                <div className="ccg-footer-actions">
                                    <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onEdit(item); }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        }
                    />
                );
            })}
        </div>
    );
};

export default InstallationCardGrid;
